import { ORDERS_URL, PAYPAL_URL, CONTIFICO_BANCO_URL } from '../constants';
import { apiSlice } from './apiSlice';

export const orderApiSlice = apiSlice.injectEndpoints({
    endpoints: (builder) => ({
        getOrderDetails: builder.query({
            query: (orderId) => ({
                url: `${ORDERS_URL}/${orderId}`,
                method: 'GET',
            }),
            providesTags: (result, error, arg) => [{ type: 'Order', id: arg }],
            keepUnusedDataFor: 5,
        }),
        createOrder: builder.mutation({
            query: (order) => ({
                url: ORDERS_URL,
                method: 'POST',
                body: order,
            }),
            invalidatesTags: ['Order'],
        }),
        createQuotation: builder.mutation({
            query: (order) => ({
                url: `${ORDERS_URL}/quotation`,
                method: 'POST',
                body: order,
            }),
            invalidatesTags: ['Order'],
        }),
        payOrder: builder.mutation({
            query: ({ orderId, companyId, details }) => ({
                url: `${ORDERS_URL}/${orderId}/pay`,
                method: 'PUT',
                body: { ...details, companyId },
            }),
            invalidatesTags: (result, error, { orderId }) => [{ type: 'Order', id: orderId }],
        }),
        createPaymentId: builder.mutation({
            query: (orderId) => ({
                url: `${ORDERS_URL}/${orderId}/payment-id`,
                method: 'POST',
            }),
        }),
        validateOrderStock: builder.mutation({
            query: (orderId) => ({
                url: `${ORDERS_URL}/${orderId}/validate-stock`,
                method: 'POST',
            }),
        }),
        updateOrder: builder.mutation({
            query: ({ orderId, ...body }) => ({
                url: `${ORDERS_URL}/${orderId}`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: (result, error, { orderId }) => [{ type: 'Order', id: orderId }],
        }),
        updateOrderItem: builder.mutation({
            query: ({ orderId, itemId, ...body }) => ({
                url: `${ORDERS_URL}/${orderId}/items/${itemId}`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: (result, error, { orderId }) => [{ type: 'Order', id: orderId }],
        }),
        updateOrderItemVariation: builder.mutation({
            query: ({ orderId, itemId, variation }) => ({
                url: `${ORDERS_URL}/${orderId}/item/${itemId}`,
                method: 'PUT',
                body: { variation },
            }),
            invalidatesTags: (result, error, { orderId }) => [{ type: 'Order', id: orderId }],
        }),
        removeOrderItem: builder.mutation({
            query: ({ orderId, itemId }) => ({
                url: `${ORDERS_URL}/${orderId}/items/${itemId}`,
                method: 'DELETE',
            }),
            invalidatesTags: (result, error, { orderId }) => [{ type: 'Order', id: orderId }],
        }),
        cancelOrder: builder.mutation({
            query: (orderId) => ({
                url: `${ORDERS_URL}/${orderId}/cancel`,
                method: 'PUT',
            }),
            invalidatesTags: (result, error, orderId) => [{ type: 'Order', id: orderId }],
        }),
        uploadPaymentProof: builder.mutation({
            query: ({ orderId, companyId, formData }) => {
                if (companyId) {
                    formData.append('companyId', companyId);
                }
                return {
                    url: `${ORDERS_URL}/${orderId}/payment-proof`,
                    method: 'POST',
                    body: formData,
                };
            },
            invalidatesTags: (result, error, { orderId }) => [{ type: 'Order', id: orderId }],
        }),
        getPaypalClientId: builder.query({
            query: () => ({
                url: `${PAYPAL_URL}`,
            }),
            keepUnusedDataFor: 5,
        }),
        getUserOrders: builder.query({
            query: (params = {}) => ({
                url: `${ORDERS_URL}/user`,
                params: Object.fromEntries(
                    Object.entries(params).filter(([_, v]) => v !== '' && v !== undefined && v !== null)
                ),
            }),
            providesTags: ['Order'],
            keepUnusedDataFor: 5,
        }),
        getOrders: builder.query({
            query: (params) => ({
                url: ORDERS_URL,
                params: Object.fromEntries(
                    Object.entries(params).filter(([_, v]) => v !== '' && v !== undefined && v !== null)
                ),
            }),
            providesTags: ['Order'],
            keepUnusedDataFor: 5,
        }),
        deliverOrder: builder.mutation({
            query: ({ orderId, companyId }) => ({
                url: `${ORDERS_URL}/${orderId}/deliver`,
                method: 'PUT',
                body: { companyId },
            }),
            invalidatesTags: (result, error, { orderId }) => [{ type: 'Order', id: orderId }],
        }),
        getBankAccounts: builder.query({
            query: () => ({
                url: CONTIFICO_BANCO_URL,
                method: 'GET',
            }),
            keepUnusedDataFor: 5,
        }),
        repeatOrder: builder.mutation({
            query: (orderId) => ({
                url: `${ORDERS_URL}/${orderId}/repeat`,
                method: 'POST',
            }),
            invalidatesTags: ['Order'],
        }),
        getInvoiceStatus: builder.query({
            query: (orderId) => ({
                url: `${ORDERS_URL}/${orderId}/invoice-status`,
            }),
            keepUnusedDataFor: 0,
        }),
        confirmPayPhoneOrder: builder.mutation({
            query: ({ id, clientTxId }) => ({
                url: `${ORDERS_URL}/payphone/confirm`,
                method: 'POST',
                body: { id, clientTxId },
            }),
            invalidatesTags: ['Order'],
        }),
        createQuotationInvoice: builder.mutation({
            query: (orderId) => ({
                url: `${ORDERS_URL}/${orderId}/quotation/invoice`,
                method: 'POST',
            }),
            invalidatesTags: (result, error, orderId) => [{ type: 'Order', id: orderId }],
        }),
        authorizeQuotationInvoice: builder.mutation({
            query: (orderId) => ({
                url: `${ORDERS_URL}/${orderId}/quotation/authorize`,
                method: 'POST',
            }),
            invalidatesTags: (result, error, orderId) => [{ type: 'Order', id: orderId }],
        }),
        retryInvoice: builder.mutation({
            query: (orderId) => ({
                url: `${ORDERS_URL}/${orderId}/contifico/retry-invoice`,
                method: 'POST',
            }),
            invalidatesTags: (result, error, orderId) => [{ type: 'Order', id: orderId }],
        }),
        registerPayment: builder.mutation({
            query: ({ orderId, cuenta_id, numero_comprobante }) => ({
                url: `${ORDERS_URL}/${orderId}/contifico/register-payment`,
                method: 'POST',
                body: { cuenta_id, numero_comprobante },
            }),
            invalidatesTags: (result, error, { orderId }) => [{ type: 'Order', id: orderId }],
        }),
        rateProductInOrder: builder.mutation({
            query: ({ orderId, productId, rating, comment }) => ({
                url: `${ORDERS_URL}/${orderId}/products/${productId}/rate`,
                method: 'POST',
                body: { rating, comment },
            }),
            invalidatesTags: (result, error, { orderId }) => [{ type: 'Order', id: orderId }],
        }),
    }),
});

export const {
    useGetOrderDetailsQuery,
    useCreateOrderMutation,
    useCreateQuotationMutation,
    useValidateOrderStockMutation,
    useUpdateOrderMutation,
    useUpdateOrderItemMutation,
    useUpdateOrderItemVariationMutation,
    useRemoveOrderItemMutation,
    useGetUserOrdersQuery,
    useGetOrdersQuery,
    useDeliverOrderMutation,
    useRepeatOrderMutation,
    useGetInvoiceStatusQuery,
    useCreateQuotationInvoiceMutation,
    useAuthorizeQuotationInvoiceMutation,
    useRetryInvoiceMutation,
    useRateProductInOrderMutation,
} = orderApiSlice;
